import { EventEmitter, Injectable } from '@angular/core';
import {
  CreateReservationsResponse,
  Price,
  SearchData,
  SearchDataDto,
  SearchDataGroup,
  SearchResult,
} from '../model/SearchData';
import { PayWayModel } from '../model/PayWayModel';
import { Station } from '../model/StationModel';
import { BusinessPremise } from '../model/Company';
import { LocalStorageDataServiceService } from './local-storage-data-service.service';
import { BuySuccess, BuySuccessReservation } from '../model/BuySuccess';
import { IssuedTicket } from 'app/shared/model/IssuedTicket';
import { Terminal } from '../model/Terminal';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { map } from 'rxjs/operators';
import { combineLatest } from 'rxjs/observable/combineLatest';

@Injectable()
export class SharedDataService {
  searchResultsChange: EventEmitter<SearchDataGroup[]> = new EventEmitter();
  selectedReturnTicketChange: EventEmitter<SearchResult> = new EventEmitter();
  pricesChange: EventEmitter<Price[]> = new EventEmitter();

  // total price
  totalPriceBehaviorSubject = new BehaviorSubject(0);
  secondsUtilExpirationSubject = new BehaviorSubject(undefined);

  get totalPrice() {
    return this.totalPriceBehaviorSubject.getValue();
  }

  set totalPrice(price: number) {
    this.totalPriceBehaviorSubject.next(price);
  }

  // terminals
  terminalsBehaviorSubject = new BehaviorSubject<Terminal[]>([]);

  get terminals() {
    return this.terminalsBehaviorSubject.getValue();
  }

  set terminals(terminals: Terminal[]) {
    this.terminalsBehaviorSubject.next(terminals);
  }

  // selected terminal
  selectedTerminalBehaviorSubject = new BehaviorSubject<Terminal>(undefined);

  get selectedTerminal() {
    return this.selectedTerminalBehaviorSubject.getValue();
  }

  set selectedTerminal(selectedTerminal: Terminal) {
    this.selectedTerminalBehaviorSubject.next(selectedTerminal);
  }

  // show fiscal
  showFiscalBehaviorSubject = new BehaviorSubject(false);

  //disableDeleteReservatio() call
  payedTickets = false;

  get showFiscal() {
    return this.showFiscalBehaviorSubject.value;
  }

  get secondsUtilExpiration() {
    return this.secondsUtilExpirationSubject.getValue();
  }

  set secondsUtilExpiration(seconds: number) {
    this.secondsUtilExpirationSubject.next(seconds);
  }

  dataObj = {};

  public searchData: SearchData;
  public payWayData: PayWayModel;
  public stations: Station[];
  private searchResults: SearchDataGroup[];
  public searchResult: SearchResult;
  public searchDataGroup: SearchDataGroup;
  private selectedReturnTicket: SearchResult;
  public createReservationsResponse: CreateReservationsResponse [];
  public createSegmentedReservationsResponse: CreateReservationsResponse [];
  private prices: Price[];
  public businessPremise: BusinessPremise;
  public saleChannel: any;
  public searchDataDto: SearchDataDto;
  public buySuccess: BuySuccess;
  public buySuccessReservation: BuySuccessReservation;
  private buyerEmail: string;

  private _issuedTickets: IssuedTicket[];

  public get issuedTickets() {
    return this._issuedTickets;
  }

  public set issuedTickets(tickets: IssuedTicket[]) {
    if (tickets.length > 0) {
      this.paid = false;
    }
    this._issuedTickets = tickets;
  }

  private _reservationGroupId: number;

  get reservationGroupId() {
    return this._reservationGroupId;
  }

  set reservationGroupId(id: number) {
    if (id !== undefined) {
      this.paid = false;
    }
    this._reservationGroupId = id;
  }

  private _paid = true;

  get paid() {
    return this._paid;
  }

  set paid(paid: boolean) {
    this._paid = paid;
  }

  public minReturnDate: Date;

  public paymentTypes = [
    { key: 'G', value: 'CASH', valueHr: 'Gotovina' },
    { key: 'K', value: 'CARD', valueHr: 'Kartica' },
    { key: 'C', value: 'CHECK', valueHr: 'Ček' },
    { key: 'T', value: 'TRANSACTION_ACCOUNT', valueHr: 'Transakcija' },
    { key: 'O', value: 'OTHER', valueHr: 'Ostalo' },
  ];


  constructor(private ls: LocalStorageDataServiceService) {
    this.searchData = new SearchData();
    this.payWayData = new PayWayModel();

    combineLatest<[Terminal, number]>([
      this.selectedTerminalBehaviorSubject,
      this.totalPriceBehaviorSubject,
    ]).pipe(
      map(
        ([selectedTerminal, totalPrice]) =>
          (selectedTerminal !== undefined && totalPrice !== undefined) &&
          (selectedTerminal.isFiscal || selectedTerminal.haveInvoice) && totalPrice > 0,
      ),
    ).subscribe(this.showFiscalBehaviorSubject);
  }

  getSearchResults() {
    return this.searchResults;
  }

  setSearchResults(searchResults: SearchDataGroup[]) {
    this.searchResults = searchResults;
    this.searchResultsChange.emit(this.searchResults);
  }

  getPrices() {
    return this.prices;
  }

  setBuyerEmail(email: string) {
    this.buyerEmail = email;
  }

  getBuyerEmail() {
    return this.buyerEmail;
  }

  setPrices(prices: Price[]) {
    this.prices = prices;
    this.pricesChange.emit(this.prices);
  }

  setSelectedReturnTicket(searchResult: SearchResult) {
    this.selectedReturnTicket = searchResult;
    this.selectedReturnTicketChange.emit(this.selectedReturnTicket);
  }

  getSelectedReturnTicket() {
    return this.selectedReturnTicket;
  }

  public saveToLocalStorage() {
    this.ls.setToDataObject('searchData', this.searchData);
    this.ls.setToDataObject('payWayData', this.payWayData);
    this.ls.setToDataObject('stations', this.stations);
    this.ls.setToDataObject('searchResults', this.searchResults);
    this.ls.setToDataObject('searchResult', this.searchResult);
    this.ls.setToDataObject('searchDataGroup', this.searchDataGroup);
    this.ls.setToDataObject('createReservationsResponse', this.createReservationsResponse);
    this.ls.setToDataObject('createSegmentedReservationsResponse', this.createSegmentedReservationsResponse);
    this.ls.setToDataObject('prices', this.prices);
    this.ls.setToDataObject('businessPremise', this.businessPremise);
    this.ls.setToDataObject('searchDataDto', this.searchDataDto);
    this.ls.setToDataObject('buySuccess', this.buySuccess);
    this.ls.setToDataObject('buySuccessReservation', this.buySuccessReservation);
    this.ls.setToDataObject('selectedReturnTicket', this.selectedReturnTicket);
  }

  private loadFromLocalStorage() {
    this.searchData = this.ls.getData('searchData');
    this.payWayData = this.ls.getData('payWayData');
    this.stations = this.ls.getData('stations');
    this.searchResults = this.ls.getData('searchResults');
    this.searchResult = this.ls.getData('searchResult');
    this.searchDataGroup = this.ls.getData('searchDataGroup');
    this.createReservationsResponse = this.ls.getData('createReservationsResponse');
    this.createSegmentedReservationsResponse = this.ls.getData('createSegmentedReservationsResponse');
    this.prices = this.ls.getData('prices');
    this.businessPremise = this.ls.getData('businessPremise');
    this.searchDataDto = this.ls.getData('searchDataDto');
    this.buySuccess = this.ls.getData('buySuccess');
    this.buySuccessReservation = this.ls.getData('buySuccessReservation');
    this.selectedReturnTicket = this.ls.getData('selectedReturnTicket');
  }

}



// WEBPACK FOOTER //
// ./src/app/shared/services/shared-data.service.ts